import React, { useState, useEffect, useRef } from "react";
import { journeyBooks } from "../../data/journeyBooks";
import "./style.css";

// Helper tối ưu URL ảnh Google Drive sang dạng thumbnail tải siêu tốc
const getOptimizedPhotoUrl = (url, width = 600) => {
  if (!url) return "";
  if (url.includes("googleusercontent.com/d/")) {
    const cleanUrl = url.split("=")[0];
    return `${cleanUrl}=w${width}`;
  }
  return url;
};

// Component từng tấm ảnh Polaroid với cơ chế Lazy Load & Skeleton Shimmer
const PolaroidCard = ({ photo, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const optimizedSrc = getOptimizedPhotoUrl(photo.url, 800);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [optimizedSrc]);

  return (
    <div
      className="polaroid-card"
      style={{ transform: `rotate(${photo.rotate || "0deg"})` }}
      onClick={() => onClick(photo)}
    >
      <div className="washi-tape-pin"></div>
      <div className="polaroid-img-box">
        {!isLoaded && !hasError && (
          <div className="polaroid-skeleton">
            <span className="skeleton-heart">📷</span>
          </div>
        )}
        <img
          ref={imgRef}
          src={optimizedSrc}
          alt={photo.caption}
          className={`polaroid-img ${isLoaded ? "is-loaded" : ""}`}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        />
      </div>
      <div className="polaroid-caption">
        <p className="caption-handwriting">{photo.caption}</p>
        <span className="caption-date-tag">{photo.date}</span>
      </div>
    </div>
  );
};

const JourneyBookshelf = () => {
  const [selectedBookIndex, setSelectedBookIndex] = useState(null);
  const [zoomedPhoto, setZoomedPhoto] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const scrollTrackRef = useRef(null);

  // Lọc sách theo phân loại (Tất cả, Cột mốc, Chuyến đi)
  const filteredBooks = journeyBooks.filter((book) => {
    if (filterCategory === "all") return true;
    return book.category === filterCategory;
  });

  const activeBook = selectedBookIndex !== null ? journeyBooks[selectedBookIndex] : null;

  // Xử lý phím Escape để đóng modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (zoomedPhoto) {
          setZoomedPhoto(null);
        } else {
          setSelectedBookIndex(null);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomedPhoto]);

  // Cuộn sang trái
  const handleScrollLeft = () => {
    if (scrollTrackRef.current) {
      scrollTrackRef.current.scrollBy({ left: -280, behavior: "smooth" });
    }
  };

  // Cuộn sang phải
  const handleScrollRight = () => {
    if (scrollTrackRef.current) {
      scrollTrackRef.current.scrollBy({ left: 280, behavior: "smooth" });
    }
  };

  // Hỗ trợ lăn chuột cuộn ngang
  const handleWheel = (e) => {
    if (scrollTrackRef.current && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      scrollTrackRef.current.scrollLeft += e.deltaY * 0.8;
    }
  };

  const handlePrevBook = () => {
    if (selectedBookIndex > 0) {
      setSelectedBookIndex(selectedBookIndex - 1);
    } else {
      setSelectedBookIndex(journeyBooks.length - 1);
    }
  };

  const handleNextBook = () => {
    if (selectedBookIndex < journeyBooks.length - 1) {
      setSelectedBookIndex(selectedBookIndex + 1);
    } else {
      setSelectedBookIndex(0);
    }
  };

  return (
    <section id="journey" className="journey-bookshelf-area section-padding">
      <div className="container">
        {/* Section Header */}
        <div className="row">
          <div className="col-12">
            <div className="section-title text-center">
              <span className="section-sub-tag">OUR TRAVEL & LIFE CHAPTERS</span>
              <h2>Kệ Sách Ký Ức & Những Chuyến Đi</h2>
              <p className="section-description">
                Mỗi chuyến đi, mỗi cột mốc là một cuốn sách mở ra những trang kỷ niệm ngọt ngào.
                <br />
                <em>Hãy chọn một cuốn sách trên kệ để cùng lật mở hành trình của chúng mình nhé!</em>
              </p>

              {/* Bộ lọc phân loại: Chuyến đi / Cột mốc / Đời thường */}
              <div className="bookshelf-category-filters">
                <button
                  className={`filter-pill ${filterCategory === "all" ? "active" : ""}`}
                  onClick={() => setFilterCategory("all")}
                >
                  ✨ Tất Cả ({journeyBooks.length})
                </button>
                <button
                  className={`filter-pill ${filterCategory === "travel" ? "active" : ""}`}
                  onClick={() => setFilterCategory("travel")}
                >
                  ✈️ Chuyến Đi ({journeyBooks.filter(b => b.category === "travel").length})
                </button>
                <button
                  className={`filter-pill ${filterCategory === "milestone" ? "active" : ""}`}
                  onClick={() => setFilterCategory("milestone")}
                >
                  💍 Cột Mốc ({journeyBooks.filter(b => b.category === "milestone").length})
                </button>
                <button
                  className={`filter-pill ${filterCategory === "daily" ? "active" : ""}`}
                  onClick={() => setFilterCategory("daily")}
                >
                  🏡 Đời Thường ({journeyBooks.filter(b => b.category === "daily").length})
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FULL-WIDTH WOODEN BOOKSHELF - TRÀN TỪ TRÁI QUA PHẢI MÀN HÌNH */}
      <div className="bookshelf-full-wrapper">
        <div className="bookshelf-spotlight-wide"></div>

        {/* Nút cuộn trái */}
        <button
          className="shelf-nav-arrow shelf-arrow-left"
          onClick={handleScrollLeft}
          title="Xem các cuốn sách trước"
          aria-label="Previous books"
        >
          ‹
        </button>

        {/* Dải cuộn sách ngang vô tận */}
        <div
          className="books-horizontal-track"
          ref={scrollTrackRef}
          onWheel={handleWheel}
        >
          <div className="books-track-inner">
            {filteredBooks.map((book) => {
              const originalIndex = journeyBooks.findIndex((b) => b.id === book.id);
              const isSelected = selectedBookIndex === originalIndex;
              return (
                <div
                  key={book.id}
                  className={`book-item-wrap ${isSelected ? "is-active" : ""}`}
                  onClick={() => setSelectedBookIndex(originalIndex)}
                  title={`Nhấn để mở: ${book.title}`}
                >
                  <div className="book-spine-card" style={{ background: book.spineColor }}>
                    <div className="spine-badge">{book.badge}</div>

                    <div className="spine-title-vertical">
                      <span className="spine-icon">{book.icon}</span>
                      <span className="spine-title-text">
                        {book.spineTitle || book.title}
                      </span>
                    </div>

                    <div className="spine-footer">
                      <span className="spine-year">{book.year}</span>
                      <span className="spine-ribbon" style={{ backgroundColor: book.accentColor }}></span>
                    </div>
                  </div>

                  <div className="book-pull-hint">
                    <span>Lật mở</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Nút cuộn phải */}
        <button
          className="shelf-nav-arrow shelf-arrow-right"
          onClick={handleScrollRight}
          title="Xem các cuốn sách tiếp theo"
          aria-label="Next books"
        >
          ›
        </button>

        {/* Thanh gỗ kệ sách full-width chạy dài từ trái qua phải */}
        <div className="wooden-shelf-full">
          <div className="shelf-trim-top"></div>
          <div className="shelf-wood-grain-full"></div>
          <div className="shelf-edge-bottom"></div>
          <div className="shelf-shadow-full"></div>
        </div>

        {/* Chú thích cuộn ngang tiện lợi */}
        <div className="shelf-scroll-indicator">
          <span>✨ Vuốt ngang hoặc nhấn mũi tên ‹ › để khám phá thêm các tập sách</span>
        </div>
      </div>

      {/* LỜI NHẮN NHỎ DỄ THƯƠNG TỪ HAI VỢ CHỒNG */}
      <div className="container">
        <div className="couple-pending-note-wrapper">
          <div className="couple-pending-note">
            <div className="note-washi-tape"></div>
            <div className="note-pin-badge">
              <span>📌 LỜI NHẮN NHỎ TỪ HAI ĐỨA</span>
            </div>
            <p className="note-message">
              “Dạo này chúng mình đang tất bật với những guồng quay công việc và vun vén cho tổ ấm nhỏ, nên tạm thời xin phép để trang web dừng chân ở đây một chút nhé. Hẹn một ngày gần nhất hai đứa sẽ lại quay trở lại và cập nhật thật bùng nổ với những chuyến đi và cột mốc mới toanh! Cảm ơn mọi người đã luôn ghé thăm và yêu thương ~ 💕✨”
            </p>
            <div className="note-sign-row">
              <span className="note-status-tag">🌱 Trang nhật ký đang tiếp tục được viết tiếp...</span>
              <span className="note-signature">Huy Hanh &amp; Linh Phương ✍️</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: Quyển Sách Kỷ Niệm Mở Ra */}
      {activeBook && (
        <div className="book-modal-backdrop" onClick={() => setSelectedBookIndex(null)}>
          <div className="book-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Thanh điều hướng nhanh trên cùng */}
            <div className="book-modal-header">
              <div className="book-step-indicator">
                <span>Tập {selectedBookIndex + 1} / {journeyBooks.length}</span>
                <span className="bullet">•</span>
                <strong>{activeBook.title}</strong>
              </div>

              <div className="book-modal-actions">
                <button className="nav-step-btn" onClick={handlePrevBook} title="Tập trước">
                  ‹ Tập trước
                </button>
                <button className="nav-step-btn" onClick={handleNextBook} title="Tập sau">
                  Tập sau ›
                </button>
                <button
                  className="close-book-btn"
                  onClick={() => setSelectedBookIndex(null)}
                  title="Đóng sách (Esc)"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Thân cuốn sách mở đôi (Scrapbook spread) */}
            <div className="scrapbook-spread">
              {/* Trang bên trái: Lời tựa nhật ký & Thông tin */}
              <div className="scrapbook-page page-left">
                <div className="scrapbook-stamp">
                  <span>TRAVEL DIARY</span>
                  <span className="stamp-icon">{activeBook.icon}</span>
                </div>

                <div className="chapter-meta">
                  <span className="meta-badge" style={{ backgroundColor: activeBook.accentColor }}>
                    {activeBook.badge}
                  </span>
                  <h3 className="chapter-title">{activeBook.title}</h3>
                  <div className="chapter-info-tags">
                    <span className="info-tag">📍 {activeBook.location}</span>
                    <span className="info-tag">📅 {activeBook.dateRange}</span>
                  </div>
                </div>

                <div className="journal-entry">
                  <div className="washi-tape tape-top-left"></div>
                  <p className="journal-text">{activeBook.blogText}</p>
                </div>

                <div className="page-signature-note">
                  <span>Ghi chép bởi:</span>
                  <strong>Huy Hanh & Linh Phương</strong>
                </div>
              </div>

              {/* Trang bên phải: Ảnh Polaroid & Lời chú thích viết tay */}
              <div className="scrapbook-page page-right">
                <div className="page-right-heading">
                  <h4>Khoảnh Khắc Ghi Dấu</h4>
                  <small>(Nhấn vào ảnh để xem to)</small>
                </div>

                <div className="polaroid-grid">
                  {activeBook.photos.map((photo, pIdx) => (
                    <PolaroidCard
                      key={pIdx}
                      photo={photo}
                      onClick={(p) => setZoomedPhoto(p)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Zoom Chi Tiết Từng Ảnh */}
      {zoomedPhoto && (
        <div className="photo-zoom-overlay" onClick={() => setZoomedPhoto(null)}>
          <div className="photo-zoom-box" onClick={(e) => e.stopPropagation()}>
            <button className="photo-zoom-close" onClick={() => setZoomedPhoto(null)}>✕</button>
            <img src={zoomedPhoto.url} alt={zoomedPhoto.caption} />
            <div className="photo-zoom-caption">
              <p>{zoomedPhoto.caption}</p>
              <span>{zoomedPhoto.date}</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default JourneyBookshelf;
